import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

// Generates a cryptographically random API key string
function generateRawKey() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return 'wlcrt_' + Array.from(array)?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
}

// SHA-256 hash using Web Crypto API
async function hashKey(rawKey) {
  const encoder = new TextEncoder();
  const data = encoder?.encode(rawKey);
  const hashBuffer = await crypto.subtle?.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
}

const ApiKeyTab = () => {
  const { organization, profile } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newKeyVisible, setNewKeyVisible] = useState(null); // raw key shown once
  const [copiedId, setCopiedId] = useState(null);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const isAdmin = profile?.role === 'admin';

  const fetchKeys = async () => {
    if (!organization?.id) return;
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase?.from('api_keys')?.select('id, name, created_at')?.eq('org_id', organization?.id)?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setApiKeys(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, [organization?.id]);

  const handleGenerate = async () => {
    if (!organization?.id || !isAdmin) return;
    setGenerating(true);
    setError(null);
    setNewKeyVisible(null);
    try {
      const rawKey = generateRawKey();
      const hash = await hashKey(rawKey);

      const { error: insertError } = await supabase?.from('api_keys')?.insert({
          org_id: organization?.id,
          key_hash: hash,
          name: 'API Key',
        });

      if (insertError) throw insertError;

      // Show the raw key once
      setNewKeyVisible(rawKey);
      await fetchKeys();
    } catch (err) {
      setError(err?.message || 'Failed to generate API key.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (keyId) => {
    if (!isAdmin) return;
    setDeletingId(keyId);
    setError(null);
    try {
      const { error: deleteError } = await supabase?.from('api_keys')?.delete()?.eq('id', keyId);
      if (deleteError) throw deleteError;
      setApiKeys(prev => prev?.filter(k => k?.id !== keyId));
      if (newKeyVisible) setNewKeyVisible(null);
    } catch (err) {
      setError(err?.message || 'Failed to delete API key.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopy = async (text, id) => {
    try {
      await navigator.clipboard?.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // fallback
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-card-foreground">API Keys</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Use your API key to send leads to Wilcart CRM from external sources. Each key is unique to your organization.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-60"
          >
            <Icon name={generating ? 'Loader2' : 'Plus'} size={16} className={generating ? 'animate-spin' : ''} />
            {generating ? 'Generating...' : 'Generate New Key'}
          </button>
        )}
      </div>
      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}
      {/* New key reveal — shown once */}
      {newKeyVisible && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
          <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
            <Icon name="AlertTriangle" size={16} />
            Copy your API key now — it will not be shown again.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-amber-200 rounded px-3 py-2 text-sm font-mono text-amber-900 break-all">
              {newKeyVisible}
            </code>
            <button
              onClick={() => handleCopy(newKeyVisible, 'new')}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-smooth"
            >
              <Icon name={copiedId === 'new' ? 'Check' : 'Copy'} size={14} />
              {copiedId === 'new' ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-amber-700">
            Use this key as: <code className="bg-amber-100 px-1 rounded">Authorization: Bearer YOUR_KEY</code>
          </p>
        </div>
      )}
      {/* Keys list */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Icon name="Loader2" size={20} className="animate-spin mr-2" />
          Loading keys...
        </div>
      ) : apiKeys?.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-border rounded-lg">
          <Icon name="Key" size={32} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No API keys yet.</p>
          {isAdmin && (
            <p className="text-xs text-muted-foreground mt-1">Click "Generate New Key" to create one.</p>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Key</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Created</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {apiKeys?.map((key) => (
                <tr key={key?.id} className="hover:bg-muted/30 transition-smooth">
                  <td className="px-4 py-3 text-card-foreground font-medium">{key?.name}</td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-muted-foreground text-xs bg-muted px-2 py-1 rounded">
                      wlcrt_••••••••••••••••
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(key?.created_at)?.toLocaleDateString()}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(key?.id)}
                        disabled={deletingId === key?.id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-destructive/10 rounded transition-smooth disabled:opacity-50"
                      >
                        <Icon name={deletingId === key?.id ? 'Loader2' : 'Trash2'} size={13} className={deletingId === key?.id ? 'animate-spin' : ''} />
                        Revoke
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Usage info */}
      <div className="p-4 bg-muted rounded-lg space-y-2">
        <p className="text-sm font-medium text-card-foreground flex items-center gap-2">
          <Icon name="Code2" size={16} className="text-primary" />
          How to use
        </p>
        <pre className="text-xs text-muted-foreground bg-background border border-border rounded p-3 overflow-x-auto whitespace-pre-wrap break-all">
{`curl -X POST https://YOUR_SUPABASE_URL/functions/v1/public-leads-api \\
  -H "Authorization: Bearer YOUR_API_KEY" \ -H"Content-Type: application/json" \\
  -d '{"name":"John Doe","email":"john@example.com","phone":"+1234567890"}'`}
        </pre>
        <p className="text-xs text-muted-foreground">
          Your <code className="bg-background border border-border px-1 rounded">org_id</code> is resolved automatically from your API key — no need to include it in the request body.
        </p>
      </div>
    </div>
  );
};

export default ApiKeyTab;
